import { html } from 'lit'

/** @type {Object<string, BrowserSupportPolicy>} */
export const supportedBrowsers = {
  BRAVE: {
    name: 'Brave',
    url: 'https://brave.com/download/',
    version: 110,
    version_android: 110,
    version_ios: 110,
    current_is_supported: true,
    version_check: getBraveVersion.bind(this, navigator.userAgent),
    render: isMobile() ? renderMobileNotSupported : renderChrome,
  },
  CHROME: {
    name: 'Chrome',
    url: 'https://www.google.com/chrome/dr/download/?brand=JSBI&geo=US&gclid=EAIaIQobChMIjLX7gNrKgAMVuNvjBx2EegaSEAAYASAAEgJDlPD_BwE&gclsrc=aw.ds',
    version: 110,
    version_android: 110,
    version_ios: 110,
    current_is_supported: true,
    version_check: getChromeVersion.bind(this, navigator.userAgent),
    render: isMobile() ? renderMobileNotSupported : renderChrome,
  },
  EDGE: {
    name: 'Edge',
    url: 'https://www.microsoft.com/en-us/edge',
    version: 110,
    version_android: 110,
    version_ios: 110,
    current_is_supported: true,
    version_check: getEdgeVersion.bind(this, navigator.userAgent),
    render: isMobile() ? renderMobileNotSupported : renderChrome,
  },
  OPERA: {
    name: 'Opera',
    url: 'https://www.opera.com/crypto/next',
    version: 95,
    version_android: 73,
    version_ios: 73,
    current_is_supported: true,
    version_check: getOperaVersion.bind(this, navigator.userAgent),
    render: isMobile() ? renderMobileNotSupported : renderChrome,
  },
  NOT_SUPPORTED: {
    name: 'Not Supported',
    url: '',
    version: 0,
    version_android: 0,
    version_ios: 0,
    current_is_supported: false,
    version_check: () => '0',
    render: isMobile() ? renderMobileNotSupported : renderNotSupported,
  },
}
/**
 * @param {string} userAgent
 * @returns {string} */
export function getChromeVersion(userAgent = navigator.userAgent) {
  return userAgent.match(/Chrom(e|ium)\/([0-9]+)\./)[2]
}
/**
 * @param {string} userAgent
 * @returns {string} */
export function getOperaVersion(userAgent = navigator.userAgent) {
  return userAgent.match(/OPR\/([0-9]+)\./)[1]
}
/**
 * @param {string} userAgent
 * @returns {string} */
export function getEdgeVersion(userAgent = navigator.userAgent) {
  return userAgent.match(/Edg\/([0-9]+)\./)[1]
}
/**
 * @param {string} userAgent
 * @returns {string} */
export function getBraveVersion(userAgent = navigator.userAgent) {
  // brave doesn't have its own user agent, so we'll use chromeiums version
  return getChromeVersion(userAgent)
}
/**
 * @param {string} userAgent
 * @returns {boolean} */
export function isMobile(userAgent = navigator.userAgent) {
  return /Android|Mobile|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    userAgent
  )
}
/** @returns {BrowserSupportPolicy} */
export function getBrowserSupport() {
  const ua = navigator.userAgent
  const isBrave = !!navigator.brave
  const isChromium = !!window.chrome
  const isOpera = isChromium && ua.indexOf('OPR') !== -1
  const isEdge = isChromium && ua.indexOf('Edg') !== -1
  const isChrome = isChromium && !isBrave && !isOpera && !isEdge

  if (isBrave) {
    return supportedBrowsers.BRAVE
  }
  if (isOpera) {
    return supportedBrowsers.OPERA
  }
  if (isEdge) {
    return supportedBrowsers.EDGE
  }
  if (isChrome) {
    return supportedBrowsers.CHROME
  }

  return supportedBrowsers.NOT_SUPPORTED
}
export function renderChrome() {
  return html`
    <style>
      a {
        text-decoration: none;
      }
      .desktop-extention-install-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
      .desktop-extention-install-title {
        margin-top: 80px;
        font-weight: bold;
        font-family: 'FLato', comic sans ms;
        font-size: 48px;
        font-style: normal;
        font-weight: 800;
        line-height: normal;
        letter-spacing: 0.028px;
        background: var(
          --kula-gradient2,
          linear-gradient(134deg, #3a7f9a 0%, #a1cee1 100%)
        );
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }
      .desktop-extention-install-text {
        color: #000;
        font-weight: 400;
        font-family: Lato;
        font-size: 32px;
        font-style: normal;
        line-height: normal;
        letter-spacing: 0.015px;
      }
      .desktop-extention-install-button {
        margin: 32px;
        display: flex;
        border: 2px solid #3b839f;
        text-align: center;
        justify-content: center;
        align-items: center;
        font: Lato;
        font-weight: 400;
        color: #3b839f;
        width: 400px;
        height: auto;
        padding: 8px;
        border-radius: var(--small-border-radius, 6px);
        box-shadow: 0px 5.37774658203125px 5.37774658203125px 0px
          rgba(0, 0, 0, 0.25);
      }
      .desktop-extention-install-button a {
        color: #3b839f;
      }
      .desktop-extention-install-button:hover {
        background-color: #3b839f;
        transition: color 0.5s ease;

        color: white;
      }
      .desktop-extention-install-button:hover a {
        color: white;
      }
      .extention-options {
        margin-top: 400px;
      }
    </style>
    <div class="desktop-extention-install-wrapper">
      <h1 class="desktop-extention-install-title">Install Browser Extension</h1>
      <p class="desktop-extention-install-text">
        To begin using the Kula Client application, please install one of the
        following Extensions.
      </p>
      <div class="extentionOptions">
        <a
          target="_blank"
          href="https://chrome.google.com/webstore/detail/coinbase-wallet-extension/hnfanknocfeofbddgcijnmhnfnkdnaad"
        >
          <button class="desktop-extention-install-button">
            Install Coinbase Wallet Extension
          </button>
        </a>
        <a target="_blank" href="https://metamask.io/download/">
          <button class="desktop-extention-install-button">
            Install Metamask Extension
          </button>
        </a>
      </div>
    </div>
  `
}
export function renderNotSupported(/** @type {string} */ browser) {
  return html`
    <style>
      .desktop-browser-support-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }

      .desktop-browser-support-title {
        margin-top: 80px;
        font-weight: bold;
        font-family: Lato;
        font-size: 48px;
        font-style: normal;
        font-weight: 800;
        line-height: normal;
        letter-spacing: 0.028px;
        background: var(
          --kula-gradient,
          linear-gradient(134deg, #3a7f9a 0%, #a1cee1 100%)
        );
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .desktop-browser-support-text {
        color: #000;
        font-weight: 400;
        font-family: Lato;
        font-size: 32px;
        font-style: normal;
        line-height: normal;
        letter-spacing: 0.015px;
      }

      .desktop-browser-support-button {
        margin: 32px;
        display: flex;
        color: white;
        border: 2px solid #3b839f;
        text-align: center;
        justify-content: center;
        align-items: center;
        font: Lato;
        font-weight: 400;
        color: #3b839f !important;
        transition: color 0.5s ease;
        width: 240px;
        padding: 8px;
        border-radius: var(--small-border-radius, 6px);
        /* background: var(--kula-dark-blue, #3B839F); */
        box-shadow: 0px 5.37774658203125px 5.37774658203125px 0px
          rgba(0, 0, 0, 0.25);
      }

      .desktop-browser-support-button a {
        text-decoration: none;
        color: #3b839f;
      }

      .desktop-browser-support-button:hover {
        background-color: #3b839f;
      }

      .desktop-browser-support-button:hover a {
        text-decoration: none;
        color: white;
      }
    </style>
    <span class="desktop-browser-support-wrapper">
      <h1 class="desktop-browser-support-title">
        Install Supported Browser to Continue
      </h1>
      <p class="desktop-browser-support-text">
        ${browser} is not supported at this time. Please use one of the
        following browsers
      </p>

      <ul>
        ${Object.values(supportedBrowsers)
          .filter((b) => b.name !== 'Not Supported')
          .map(
            (b) =>
              html`<li class="desktop-browser-support-button">
                <a
                  class="desktop-browser-support-a"
                  target="_blank"
                  href="${b.url}"
                  >${b.name}</a
                >
              </li>`
          )}
      </ul>
    </span>
  `
}
export function renderMobileNotSupported() {
  return html` <style>
      .mobile-browser-support-wrapper {
        margin: 16px;
      }

      .mobile-browser-support-a {
        text-decoration: none !important;
        text-align: center !important;
      }

      .mobile-browser-options-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin: 32px 0px 16px 0px;
      }

      .mobile-browser-options-wrapper button {
        display: flex;
        justify-content: center;
        align-items: center;
        margin-bottom: 16px;
      }

      .mobile-browser-not-supported-text {
        color: #000;
        text-align: center;
        margin: auto;
        width: 292px;
        font-family: Lato;
        font-size: 16px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
        letter-spacing: 0.015px;
      }

      .mobile-browser-support-heading {
        margin-top: 80px;
        text-align: center;
        font-family: Lato;
        font-size: 32px;
        font-style: normal;
        font-weight: 800;
        line-height: normal;
        letter-spacing: 0.028px;
        background: linear-gradient(134deg, #3a7f9a 0%, #a1cee1 100%);
        background-clip: text;
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .mobile-browser-support-button {
        display: flex;
        font-size: 16px;
        color: white !important;
        width: 180px;
        padding: 8px;
        justify-content: center;
        align-items: center;
        gap: 8px;
        border-radius: var(--small-border-radius, 6px);
        background: var(--kula-dark-blue, #3b839f);
        box-shadow: 0px 5.37774658203125px 5.37774658203125px 0px
          rgba(0, 0, 0, 0.25);
      }
    </style>
    <div class="mobile-browser-support-wrapper">
      <h1 class="mobile-browser-support-heading">
        Connect to supported browser to continue
      </h1>
      <p class="mobile-browser-not-supported-text">
        This browser is not supported for mobile. Please use one of the
        following supported providers.
      </p>

      <div class="mobile-browser-options-wrapper">
        <a
          class="mobile-browser-support-a"
          href="https://www.opera.com/download"
        >
          <button class="mobile-browser-support-button">Opera Mobile</button>
        </a>
        <a
          class="mobile-browser-support-a"
          href="https://metamask.app.link/dapp/${window.location.href}"
        >
          <button class="mobile-browser-support-button">Metamask</button>
        </a>
        <a
          class="mobile-browser-support-a"
          href="https://go.cb-w.com/dapp?cb_url=${window.location.href}"
        >
          <button class="mobile-browser-support-button">
            Coinbase Wallets
          </button>
        </a>
      </div>
    </div>`
}
